import React, { useState, useEffect } from "react";

const getRandomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

const FractionShapes = () => {
    const [totalParts, setTotalParts] = useState(5);
    const [filledParts, setFilledParts] = useState(2);
    const [options, setOptions] = useState<string[]>([]);
    const [message, setMessage] = useState<string>("");
    const [shapeType, setShapeType] = useState<"circle" | "square" | "rectangle">("circle");

    // Function to generate a new question
    const generateNewQuestion = () => {
        const randomTotalParts = getRandomInt(3, 7); // Between 3 and 7
        const randomFilledParts = getRandomInt(1, randomTotalParts - 1);

        setTotalParts(randomTotalParts);
        setFilledParts(randomFilledParts);
        setMessage(""); // Reset message

        // Randomly select shape type
        const shapes = ["circle", "square", "rectangle"];
        setShapeType(shapes[getRandomInt(0, 2)] as "circle" | "square" | "rectangle");

        // Generate 3 incorrect options
        const incorrectOptions = new Set<string>();

        while (incorrectOptions.size < 3) {
            const randomNumerator = getRandomInt(1, randomTotalParts);
            const randomDenominator = getRandomInt(3, 7);
            if (randomNumerator !== randomDenominator && randomNumerator !== filledParts) {
                incorrectOptions.add(`${randomNumerator}/${randomDenominator}`);
            }
        }

        // Combine correct answer with incorrect ones and shuffle
        const answerOptions: string[] = [...Array.from(incorrectOptions), `${randomFilledParts}/${randomTotalParts}`];

        setOptions(answerOptions.sort(() => Math.random() - 0.5));
    };

    useEffect(() => {
        generateNewQuestion(); // Generate first question
        document.title = "Mamas Guidance - Class One - Mathematics - Fraction - Choose Correct"; // Set page title
    }, []);

    // Function to handle option click
    const handleOptionClick = (selectedOption: string) => {
        const correctAnswer = `${filledParts}/${totalParts}`;

        if (selectedOption === correctAnswer) {
            setMessage("✅ Correct! Generating new question...");
            setTimeout(generateNewQuestion, 1000); // Wait 1 sec then generate new question
        } else {
            setMessage("❌ Wrong answer! Try again.");
        }
    };

    const radius = 50;
    const center = 50;

    // Function to calculate SVG paths for slices (for circles)
    const generateCircleSlices = () => {
        const slices = [];
        const anglePerSlice = 360 / totalParts;

        for (let i = 0; i < totalParts; i++) {
            const startAngle = i * anglePerSlice;
            const endAngle = startAngle + anglePerSlice;

            const toRadians = (angle: number) => (angle * Math.PI) / 180;

            const x1 = center + radius * Math.cos(toRadians(startAngle));
            const y1 = center + radius * Math.sin(toRadians(startAngle));
            const x2 = center + radius * Math.cos(toRadians(endAngle));
            const y2 = center + radius * Math.sin(toRadians(endAngle));

            const largeArcFlag = anglePerSlice > 180 ? 1 : 0;

            slices.push(
                <path
                    key={i}
                    d={`M ${center},${center} L ${x1},${y1} A ${radius},${radius} 0 ${largeArcFlag} 1 ${x2},${y2} Z`}
                    fill={i < filledParts ? "orange" : "lightgray"}
                    stroke="black"
                />
            );
        }
        return slices;
    };

    // Function to generate square partitions
    const renderSquarePartitions = () => {
        const size = 100;
        const cx = size / 2;
        const cy = size / 2;
        const angleStep = (2 * Math.PI) / totalParts;
        const paths = [];

        for (let i = 0; i < totalParts; i++) {
            const angle1 = i * angleStep;
            const angle2 = (i + 1) * angleStep;
            const x1 = cx + cx * Math.cos(angle1);
            const y1 = cy + cy * Math.sin(angle1);
            const x2 = cx + cx * Math.cos(angle2);
            const y2 = cy + cy * Math.sin(angle2);
            const isFilled = i < filledParts;

            paths.push(
                <polygon
                    key={i}
                    points={`${cx},${cy} ${x1},${y1} ${x2},${y2}`}
                    fill={isFilled ? "orange" : "lightgray"}
                    stroke="black"
                />
            );
        }
        return paths;
    };

    // Function to generate rectangle partitions
    const generateRectanglePartitions = () => {
        const width = 120;
        const height = 60;
        const cols = totalParts; // Splitting the rectangle into vertical strips
        const rectWidth = width / cols;
        const rects = [];

        for (let i = 0; i < totalParts; i++) {
            const isFilled = i < filledParts;

            rects.push(
                <rect
                    key={i}
                    x={i * rectWidth}
                    y={0}
                    width={rectWidth}
                    height={height}
                    fill={isFilled ? "orange" : "lightgray"}
                    stroke="black"
                />
            );
        }
        return rects;
    };

    return (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
            <h3>What fraction is filled?</h3>

            {shapeType === "circle" ? (
                <svg width="100" height="100" viewBox="0 0 100 100">
                    {generateCircleSlices()}
                </svg>
            ) : shapeType === "square" ? (
                <svg width="100" height="100" viewBox="0 0 100 100">
                    {renderSquarePartitions()}
                </svg>
            ) : (
                <svg width="120" height="60" viewBox="0 0 120 60">
                    {generateRectanglePartitions()}
                </svg>
            )}

            <div style={{ margin: "20px 0", display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
                {options.map((option, index) => (
                    <div className="background-blue"
                        key={index}
                        style={{
                            margin: "5px",
                            padding: "10px",
                            fontSize: "16px",
                            cursor: "pointer",
                        }}
                        onClick={() => handleOptionClick(option)}
                    >
                        {option}
                    </div>
                ))}
            </div>

            <p style={{ fontWeight: "bold", marginTop: "10px" }}>{message}</p>
        </div>
    );
};

export default FractionShapes;
